import { HashLink } from "@/components/HashLink";
import { LoginDialog } from "@/components/LoginDialog";
import { ProfileDialog } from "@/components/ProfileDialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MEGA_MENU_EXTERNAL_URLS } from "@/i18n/menu-external-urls";
import type { UserInfo } from "@/lib/api/auth";
import { useLang, useT } from "@/lib/i18n";
import type { PlanId } from "@/lib/pricing";
import { products } from "@/lib/sections";
import {
  clearAuth,
  getUserInfo as getCachedUserInfo,
  getToken,
  saveUserInfo,
} from "@/lib/utils/auth-storage";
import { isSubAppKey } from "@/lib/config";
import { captureInviteCodeFromURL } from "@/lib/utils/invite-code";
import { navigateToSubApp } from "@/lib/utils/sso";
import { useTheme } from "@/contexts/ThemeContext";
import { LogOut, UserRound } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

type LinkItem = { label: string; href: string };
type FlatMenu = { label: string; href?: string; items: LinkItem[] };
type GroupedMenu = {
  label: string;
  href?: string;
  groups: { header: string; items: LinkItem[] }[];
};
type LinkOnlyMenu = { label: string; href: string };
type Menu = FlatMenu | GroupedMenu | LinkOnlyMenu;

interface HeaderProps {
  activeTab?: string;
  setActiveTab?: (tab: string) => void;
  currentPlanId?: PlanId;
  sidebarOpen?: boolean;
  setSidebarOpen?: (open: boolean) => void;
  isVisible?: boolean;
  setLibrarySubTab?: (tab: "video" | "voice" | "model") => void;
  loginDialogTrigger?: number;
  postLoginRedirect?: string | null;
}

/**
 * UI 原型 SITE NAV 1:1 精确重构导航栏
 * 包含：Logo、居中主导航(下拉菜单+箭头动效)、小行星主题切换胶囊、双语切换胶囊、登录/用户头像
 */
const Header: React.FC<HeaderProps> = ({ loginDialogTrigger = 0, postLoginRedirect }) => {
  const { lang, setLang } = useLang();
  const { theme, toggleTheme } = useTheme();
  const t = useT();

  const getInitialModeFromURL = (): "signin" | "signup" | "forgot" => {
    try {
      const url = new URL(window.location.href);
      if (url.pathname === "/register") return "signup";
    } catch { /* ignore */ }
    return "signin";
  };

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogInitialMode, setDialogInitialMode] = useState<"signin" | "signup" | "forgot">(getInitialModeFromURL());
  const [user, setUser] = useState<UserInfo | null>(null);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const postLoginRedirectRef = useRef<string | null>(null);
  const prevLoginTriggerRef = useRef(0);

  // Restore user from localStorage on mount
  useEffect(() => {
    const cached = getCachedUserInfo();
    if (cached && getToken()) setUser(cached);
  }, []);

  // Cross-tab sync
  useEffect(() => {
    const onStorage = () => {
      const cached = getCachedUserInfo();
      setUser(cached && getToken() ? cached : null);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Sync postLoginRedirect
  useEffect(() => {
    if (postLoginRedirect) {
      postLoginRedirectRef.current = postLoginRedirect;
    }
  }, [postLoginRedirect]);

  // URL deep link handling
  useEffect(() => {
    const url = new URL(window.location.href);
    let needReplace = false;
    let shouldOpenDialog = false;
    let dialogMode: "signin" | "signup" | "forgot" | null = null;

    if (captureInviteCodeFromURL(url)) {
      needReplace = true;
    }

    if (url.pathname === "/register") {
      dialogMode = "signup";
      shouldOpenDialog = true;
      url.pathname = "/";
      needReplace = true;
    }

    // 子应用主动退出时，先清理门户域名下的本地认证状态。
    if (url.searchParams.get("logout") === "1") {
      clearAuth();
      setUser(null);
      url.searchParams.delete("logout");
      needReplace = true;
    }

    const loginParam = url.searchParams.get("login");
    if (loginParam === "1") {
      postLoginRedirectRef.current = "back";
      sessionStorage.setItem("loginRedirect", "back");
      shouldOpenDialog = true;
      url.searchParams.delete("login");
      needReplace = true;
    } else if (loginParam && isSubAppKey(loginParam)) {
      if (getToken()) {
        url.searchParams.delete("login");
        window.history.replaceState({}, "", url.toString());
        navigateToSubApp(loginParam).catch(() => {});
        return;
      }
      postLoginRedirectRef.current = loginParam;
      sessionStorage.setItem("loginRedirect", loginParam);
      shouldOpenDialog = true;
      url.searchParams.delete("login");
      needReplace = true;
    }

    if (shouldOpenDialog) {
      if (dialogMode) {
        setDialogInitialMode((prev) => (dialogMode !== prev ? dialogMode : prev));
      }
      setDialogOpen(true);
    }

    if (needReplace) {
      window.history.replaceState({}, "", url.toString());
    }
  }, []);

  useEffect(() => {
    if (loginDialogTrigger <= prevLoginTriggerRef.current) return;
    prevLoginTriggerRef.current = loginDialogTrigger;
    setDialogOpen(true);
  }, [loginDialogTrigger]);

  const handleLoginSuccess = (next: UserInfo) => {
    setUser(next);
    const redirect = postLoginRedirectRef.current || sessionStorage.getItem("loginRedirect");
    postLoginRedirectRef.current = null;
    sessionStorage.removeItem("loginRedirect");
    if (!redirect) return;
    setTimeout(async () => {
      if (redirect === "back") {
        window.history.back();
      } else if (isSubAppKey(redirect)) {
        try {
          await navigateToSubApp(redirect);
        } catch {}
      }
    }, 300);
  };

  const handleSignOut = () => {
    setProfileDialogOpen(false);
    clearAuth();
    setUser(null);
  };

  const handleProfileSaved = (nextUser: UserInfo) => {
    setUser(nextUser);
    saveUserInfo(nextUser);
  };

  const getInitials = (name: string) => name.slice(0, 1).toUpperCase();

  const menus: Menu[] = [
    {
      label: t("Products", "产品"),
      href: "/products",
      items: [
        { label: "OranHub", href: "/products#oranhub" },
        { label: "OranSim", href: "/products#oransim" },
        { label: "OranGen", href: "/products#orangen" },
        { label: "OranMed", href: "/products#oranmed" },
        { label: "OranGEO", href: MEGA_MENU_EXTERNAL_URLS["menu.prod.orangeo"] || "/products#orangeo" },
        { label: "OranMemory", href: "/products#oranmemory" },
      ],
    },
    {
      label: t("Solutions", "解决方案"),
      href: "/solutions",
    },
    {
      label: t("Platform", "平台"),
      href: "/models",
      items: [
        { label: t("Data Center", "数据中台"), href: MEGA_MENU_EXTERNAL_URLS["menu.plat.dataCenter"] || "#" },
        { label: t("Model Library", "模型库"), href: "https://models.photog.art" },
        { label: t("Skill Library", "技能库"), href: MEGA_MENU_EXTERNAL_URLS["menu.plat.skills"] || "#" },
        { label: t("API Docs", "接口文档"), href: "#" },
      ],
    },
    {
      label: t("Inspiration", "灵感"),
      href: "/library",
      items: [
        { label: t("Docs", "文档"), href: "/library" },
        { label: t("Blog", "博客"), href: "/library?tab=blog" },
        { label: t("Research", "研究"), href: "/library?tab=research" },
        { label: t("Cases", "案例"), href: "/library?tab=case" },
      ],
    },
    {
      label: t("Company", "公司"),
      href: "/company",
      items: [
        { label: t("About Us", "关于我们"), href: "/company#about" },
        { label: t("News", "新闻"), href: "/company#news" },
        { label: t("Careers", "招聘"), href: "/company#careers" },
        { label: t("Contact Us", "联系我们"), href: "/company#contact" },
      ],
    },
    {
      label: t("Pricing", "定价"),
      href: "/pricing",
    },
  ];

  const renderItem = (i: LinkItem) => {
    const className =
      "flex items-center min-h-[38px] px-3.5 py-2 rounded-[10px] text-[14.5px] sm:text-[15px] font-medium text-[#F2ECE5]/75 hover:text-[#FDBA74] hover:bg-[#F97316]/14 transition-all duration-150 whitespace-nowrap";
    if (i.href === "#") {
      return (
        <a href="#" className={className}>
          {i.label}
        </a>
      );
    }
    if (/^https?:\/\//.test(i.href)) {
      return (
        <a href={i.href} target="_blank" rel="noopener noreferrer" className={className}>
          {i.label}
        </a>
      );
    }
    return (
      <HashLink to={i.href} className={className}>
        {i.label}
      </HashLink>
    );
  };

  return (
    <>
      <header
        className="fixed inset-x-0 top-0 z-[300] flex items-center justify-between h-16 border-b border-white/[0.09] bg-[#0B0907]/88 backdrop-blur-xl transition-all"
        style={{
          paddingLeft: "clamp(24px, 3.2vw, 48px)",
          paddingRight: "clamp(24px, 3.2vw, 48px)",
          gap: "clamp(12px, 2vw, 28px)",
        }}
      >
        {/* Logo */}
        <Link
          to="/"
          className="flex items-center gap-2.5 font-bold text-[19px] tracking-wide text-[#F2ECE5] shrink-0 hover:opacity-90 transition-opacity"
          aria-label="OranAI 首页"
        >
          <img
            src="https://photog-test.oss-accelerate.aliyuncs.com/uploads/public/report/20260827/29a2395a291e4c06b87f68a1970822cb.png"
            alt="OranAI"
            className="w-[30px] h-[30px] object-contain shrink-0"
            onError={(e) => {
              // 备用本地 logo
              (e.target as HTMLImageElement).src = "/logo.svg";
            }}
          />
          <span className="font-bold tracking-tight">OranAI</span>
        </Link>

        {/* Center Main Nav (.snav-nav) */}
        <nav className="hidden lg:flex items-center gap-1 mx-auto" aria-label="主导航">
          {menus.map((m) =>
            "items" in m ? (
              <div key={m.label} className="relative group">
                <Link
                  to={m.href || "#"}
                  className="flex items-center justify-center gap-1.5 px-3.5 py-2 rounded-lg text-[14.5px] font-medium text-[#F2ECE5]/70 hover:text-[#F2ECE5] hover:bg-white/[0.06] transition-all duration-200"
                >
                  <span>{m.label}</span>
                  <span className="text-[10px] text-[#F97316]/75 group-hover:translate-y-0.5 transition-transform duration-200">
                    ▾
                  </span>
                </Link>

                {/* Submenu (.nav-menu) */}
                <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2.5 min-w-[200px] opacity-0 invisible -translate-y-1.5 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-200 pointer-events-none group-hover:pointer-events-auto">
                  <ul className="list-none p-2.5 rounded-[16px] bg-[#120E0A] border border-white/[0.09] shadow-[0_22px_60px_rgba(0,0,0,0.5)] flex flex-col gap-1">
                    {m.items.map((item) => (
                      <li key={item.label}>{renderItem(item)}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ) : (
              <Link
                key={m.label}
                to={m.href}
                className="flex items-center justify-center px-3.5 py-2 rounded-lg text-[14.5px] font-medium text-[#F2ECE5]/70 hover:text-[#F2ECE5] hover:bg-white/[0.06] transition-all duration-200"
              >
                {m.label}
              </Link>
            )
          )}
        </nav>

        {/* Right Actions (.snav-right) */}
        <div className="flex items-center gap-2.5 shrink-0">
          {/* Dual Language Switcher (.lang) */}
          <div className="flex items-center p-0.5 rounded-[8px] bg-white/[0.08] text-xs">
            <button
              type="button"
              onClick={() => setLang("en")}
              aria-pressed={lang === "en"}
              className={`px-2.5 py-1 rounded-[6px] text-xs font-medium transition-all duration-200 ${
                lang === "en"
                  ? "bg-[#F2ECE5] text-[#0B0907] font-semibold shadow-sm"
                  : "text-[#F2ECE5]/60 hover:text-[#F2ECE5]"
              }`}
            >
              EN
            </button>
            <button
              type="button"
              onClick={() => setLang("zh")}
              aria-pressed={lang === "zh"}
              className={`px-2.5 py-1 rounded-[6px] text-xs font-medium transition-all duration-200 ${
                lang === "zh"
                  ? "bg-[#F2ECE5] text-[#0B0907] font-semibold shadow-sm"
                  : "text-[#F2ECE5]/60 hover:text-[#F2ECE5]"
              }`}
            >
              中
            </button>
          </div>

          {/* User Account / Login Button (.snav-login) */}
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F97316] focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                  aria-label={t("Account menu", "账号菜单")}
                >
                  <Avatar className="w-8 h-8 cursor-pointer hover:ring-2 hover:ring-[#F97316]/60 transition-all">
                    {user.avatar && (
                      <AvatarImage src={user.avatar} alt={user.nickname || user.username} />
                    )}
                    <AvatarFallback className="bg-[#F97316] text-black text-xs font-bold">
                      {getInitials(user.nickname || user.username)}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                sideOffset={8}
                className="w-72 rounded-2xl border border-white/10 bg-[#120E0A]/95 p-2 shadow-2xl backdrop-blur-xl"
              >
                <DropdownMenuLabel className="flex items-center gap-3 px-2 py-2 font-normal">
                  <Avatar className="h-10 w-10 border border-white/10">
                    {user.avatar && (
                      <AvatarImage src={user.avatar} alt={user.nickname || user.username} />
                    )}
                    <AvatarFallback className="bg-[#F97316] text-xs font-bold text-black">
                      {getInitials(user.nickname || user.username)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-[#F2ECE5]">
                      {user.nickname || user.username}
                    </div>
                    <div className="truncate text-xs text-[#F2ECE5]/50">{user.email}</div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem
                  className="cursor-pointer gap-2 rounded-xl px-3 py-2.5 text-[#F2ECE5] hover:bg-white/5"
                  onSelect={() => setProfileDialogOpen(true)}
                >
                  <UserRound className="h-4 w-4 shrink-0 text-[#F97316]" aria-hidden />
                  <span>{t("Personal profile", "个人中心")}</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="cursor-pointer gap-2 rounded-xl px-3 py-2.5 text-[#FF6B6B] hover:bg-[#FF4D4F]/15"
                  onSelect={handleSignOut}
                >
                  <LogOut className="h-4 w-4 shrink-0" aria-hidden />
                  <span>{t("Sign out", "退出登录")}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <button
              type="button"
              onClick={() => setDialogOpen(true)}
              className="snav-login inline-flex items-center justify-center px-5 py-2 rounded-full font-medium text-[13px] text-[#F2ECE5] bg-white/[0.10] border border-white/[0.09] hover:bg-[#F97316] hover:text-[#180C02] hover:border-[#F97316] transition-all duration-200 active:scale-[0.98]"
            >
              {t("Sign In", "登录")}
            </button>
          )}
        </div>
      </header>

      {/* Login Dialog & Profile Dialog */}
      <LoginDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSuccess={handleLoginSuccess}
        initialMode={dialogInitialMode}
      />
      {user && (
        <ProfileDialog
          open={profileDialogOpen}
          onOpenChange={setProfileDialogOpen}
          user={user}
          onSaved={handleProfileSaved}
        />
      )}
    </>
  );
};

export default Header;
